import React from "react";
import { Link } from "react-router-dom";
import { ChefHat, Bath, Layers, Building2, Home as HomeIcon, TreePine, ArrowRight, Check } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CTABand } from "../components/Layout";
import { Seo } from "../components/Seo";
import { SERVICES } from "../data";

const ICONS = { ChefHat, Bath, Layers, Building2, Home: HomeIcon, TreePine };

const PROCESS = [
  { step: "01", title: "Consult & Quote", text: "We visit your home, understand your goals, and provide a clear, itemized estimate." },
  { step: "02", title: "Design & Plan", text: "We finalize materials, drawings and permits so there are no surprises down the road." },
  { step: "03", title: "Build & Craft", text: "Our tradespeople execute with precision, keeping your site clean and communication open." },
  { step: "04", title: "Reveal & Warranty", text: "A detailed walkthrough, a spotless finish, and workmanship backed by our warranty." },
];

const Services = () => (
  <div data-testid="services-page">
    <Seo
      title="Renovation Services | Bright Oak Renovations"
      description="Kitchen & bathroom renovations, basement finishing, home additions, whole-home remodels and exterior/deck work across Waterloo Region, Ontario."
      path="/services"
    />
    <PageHeader
      label="Our Services"
      title="Craftsmanship for every corner of your home"
      subtitle="From focused single-room remodels to full structural additions, Bright Oak delivers general contracting services with one trusted team accountable from first sketch to final reveal."
      testid="services-header"
    />

    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
      <div className="space-y-6">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          const reversed = i % 2 === 1;
          return (
            <div
              key={s.slug}
              data-testid={`service-item-${s.slug}`}
              className={`grid md:grid-cols-2 gap-0 bg-white border border-[#2B2B2B]/10 overflow-hidden ${reversed ? "md:[direction:rtl]" : ""}`}
            >
              <div className="h-64 md:h-auto overflow-hidden group">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center [direction:ltr]">
                <Icon size={34} strokeWidth={1.5} className="text-[#C68B4E]" />
                <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight mt-5">{s.title}</h2>
                <p className="text-[#5C5C5C] mt-4 leading-relaxed">{s.description}</p>
                <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#C68B4E] hover:gap-3 transition-all w-fit" data-testid={`service-quote-${s.slug}`}>
                  Request a Quote <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>

    {/* PROCESS */}
    <section className="bg-white border-y border-[#2B2B2B]/10 py-20 lg:py-28" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <p className="section-label">How We Work</p>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 mb-14">A process built on clarity</h2>
        <div className="grid md:grid-cols-4 gap-8">
          {PROCESS.map((p) => (
            <div key={p.step} className="border-t-2 border-[#C68B4E] pt-6">
              <div className="text-4xl font-extrabold text-[#7A8B6F]/40">{p.step}</div>
              <h3 className="text-lg font-bold mt-3">{p.title}</h3>
              <p className="text-sm text-[#5C5C5C] mt-3 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
      <div className="bg-[#7A8B6F]/10 border-l-4 border-[#7A8B6F] p-8 lg:p-12">
        <h3 className="text-2xl font-extrabold tracking-tight">Every project includes</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {["Licensed & insured trades", "Transparent fixed quotes", "Dedicated project manager", "Permit & inspection handling", "Daily site cleanup", "Workmanship warranty"].map((f) => (
            <div key={f} className="flex items-center gap-3">
              <span className="w-6 h-6 bg-[#C68B4E] flex items-center justify-center shrink-0">
                <Check size={15} className="text-white" strokeWidth={2.5} />
              </span>
              <span className="text-sm font-semibold">{f}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    <CTABand />
  </div>
);

export default Services;
